import { AppSettingsService } from 'src/app/settings/services/app-settings.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { animate, style, transition, trigger } from '@angular/animations'
import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { LanguageService } from 'src/app/language.service'
import { VendorService } from 'src/app/multivendor/vendor.service'

@Component({
  selector: 'app-social-login',
  templateUrl: './social-login.component.html',
  styleUrls: ['./social-login.component.scss'],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden', marginTop: 0 }),
        animate('250ms ease-out', style({ height: '*', opacity: 1, marginTop: '8px' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: 0, opacity: 0, overflow: 'hidden', marginTop: 0 }))
      ])
    ])
  ]
})
export class SocialLoginComponent implements OnInit {
  auth0_clientId: string = ''
  auth0_domain: string = ''
  auth0_redirect_uri: string = ''
  phoneLoginEnabled: boolean = false
  requiresApproval: boolean = false
  auth0_connection: string = ''
  disableSocialLogin: boolean = false
  disableSignup: boolean = false
  deviceLock: boolean = false
  error: string | null = null
  generalSettingsData: any = {}
  social!: FormGroup
  productProgress: boolean = false
  guestCheckOut: boolean = false
  originsCopied: boolean = false
  settingsForm = this.fb.group({
    application_name: '',
    package_name: '',
    ipa_sku_number: '',
    application_description: '',
    icon: '',
    splash_screen: '',
    release_app: ''
  })

  constructor(
    private appSettingsService: AppSettingsService,
    public builderService: BuilderService,
    private vendor: VendorService,
    private fb: FormBuilder,
    private languageService: LanguageService,
  ) {}

  ngOnInit() {
    this.social = this.fb.group({})
    this.getApplicationSettingsData()
  }

  saveToApplicationSettings() {
    this.productProgress = true
    const projectId = this.builderService.selectedProject

    this.appSettingsService.getApplicationSettings(projectId).subscribe(
      (currentSettings: any) => {
        const updatedSettings = {
          ...currentSettings,
          auth0_domain: this.auth0_domain,
          auth0_redirect_uri: this.auth0_redirect_uri,
          auth0_clientId: this.auth0_clientId,
          disableSocialLogin: this.disableSocialLogin,
          disableSignup: this.disableSignup,
          guestCheckOut: this.guestCheckOut,
          phoneLoginEnabled: this.phoneLoginEnabled,
          requiresApproval: this.requiresApproval,
          auth0_connection: this.auth0_connection,
          deviceLock: this.deviceLock
        }

        this.appSettingsService.setApplicationSettings(projectId, updatedSettings).subscribe(
          () => {
            this.appSettingsService.setWPApplicationSettings(this.builderService.selectedProject, updatedSettings).subscribe(
              () => {
                if (this.vendor.isMultiVendor) this.vendor.syncSuperVendorSettingsToVendors()
                this.getInitialSettings()
              },
              (error: any) => {
                console.error('Error syncing social login data', error)
                this.productProgress = false
              }
            )
          },
          (error: any) => {
            console.error('Error updating social login data', error)
            this.productProgress = false
          }
        )
      },
      (error: any) => {
        console.error('Error saving social login data', error)
        this.productProgress = false
      }
    )
  }

  getApplicationSettingsData() {
    const projectId = this.builderService.selectedProject
    this.appSettingsService.getApplicationSettings(projectId).subscribe(
      (data: any) => {
        if (data) {
          this.auth0_domain = data.auth0_domain || 'dev-l0pw2od54vx31tmu.us.auth0.com'
          this.auth0_redirect_uri = data.auth0_redirect_uri || 'com.stacks.www://dev-l0pw2od54vx31tmu.us.auth0.com/capacitor/com.stacks.www/callback'
          this.auth0_clientId = data.auth0_clientId || 'Q3L5Nm4FxEPTirlszbhS3KEz3jRsOnnH'
          this.guestCheckOut = data.guestCheckOut ?? false
          this.phoneLoginEnabled = data.phoneLoginEnabled || false
          this.disableSocialLogin = data.disableSocialLogin || false
          this.disableSignup = data.disableSignup || false
          this.requiresApproval = data.requiresApproval || false,
          this.auth0_connection = data.auth0_connection || '',
          this.deviceLock = data.deviceLock ?? false
          this.getInitialSettings()
        } else {
          console.error('No data found')
        }
      },
      (error: any) => {
        console.error('Error loading data', error)
      }
    )
  }

  getInitialSettings = () => {
    let project_id = this.builderService.selectedProject
    this.appSettingsService.getGeneralSettings(project_id).subscribe(
      (settings: any) => {
        this.generalSettingsData = settings !== null ? settings : {}
        this.builderService.showUpdateProgress = false
        const redirectUriParts = this.auth0_redirect_uri.split('://')
        const firstPart = redirectUriParts[0]
        if (this.generalSettingsData.package_name !== firstPart) {
          this.error = `${this.languageService.translate("Your sign-in link needs to begin with your app's package name.")} ("${this.generalSettingsData.package_name}") ${this.languageService.translate('Please update it so your customers can log in.')}`
        } else {
          this.error = null
        }
        const lastPart = this.auth0_redirect_uri.split('capacitor/')[1]?.split('/')[0] // Extract the last part
        if (this.generalSettingsData.package_name !== lastPart) {
          this.error = `${this.languageService.translate("Your sign-in link also needs to include your app's package name in the middle.")} ("${this.generalSettingsData.package_name}") ${this.languageService.translate('Please update it so your customers can log in.')}`
        }
        this.productProgress = false
      },
      (err) => {
        console.log(err)
        this.productProgress = false
      }
    )
  }

  copyToClipboard(): void {
    const textToCopy = (document.getElementById('textToCopy') as HTMLElement)?.innerText
    if (textToCopy) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          this.originsCopied = true
          setTimeout(() => (this.originsCopied = false), 2000)
        })
        .catch((err) => {
          console.error('Error copying text:', err)
        })
    } else {
      console.error('No text found to copy')
    }
  }
}
